import { useContext, useEffect, useMemo, useState } from 'react';

import { ServiceCountry } from '@upside/browser-rest-client/lib/service-country-storage';
import { Button, Card, Icon, List } from '@upside/flux-ui';

import { faCaretDown } from '@fortawesome/pro-regular-svg-icons';
import * as Select from '@radix-ui/react-select';

import { AppContext } from '../../context/App';
import { useGetTranslatedRegionOptions } from './hooks/useGetTranslatedRegionOptions';

export function RegionSelect({
  container,
}: {
  container: HTMLDivElement | null;
}) {
  const context = useContext(AppContext);
  const options = useGetTranslatedRegionOptions();

  const [region, setRegion] = useState<string>(
    () => context?.authentication.serviceCountryStorage.get() || 'gb',
  );

  useEffect(() => {
    const currentRegion = context?.authentication.serviceCountryStorage.get();
    if (currentRegion && currentRegion !== region) {
      setRegion(currentRegion);
    }
  }, [context, region]);

  const selectedOption = useMemo(
    () => options.find((item) => item.region === region) ?? options[0],
    [options, region],
  );

  const [keyboardUsed, setKeyboardUsed] = useState(false);
  useEffect(() => {
    const onKeyDown = () => setKeyboardUsed(true);
    const onMouseDown = () => setKeyboardUsed(false);

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('mousedown', onMouseDown);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('mousedown', onMouseDown);
    };
  }, []);

  const onCloseAutoFocus = (e: any) => {
    if (!keyboardUsed) {
      e.preventDefault();
    }
  };

  return (
    <Select.Root
      value={region}
      onValueChange={(val: string) => {
        context?.authentication.serviceCountryStorage.set(
          val as ServiceCountry,
        );
        setRegion(val);
      }}
    >
      <Select.Trigger asChild>
        <Button colour="neutral" variant="ghost" radius="md">
          {selectedOption.icon && <Button.Icon icon={selectedOption.icon} />}
          {selectedOption.svg && (
            <img
              className="pl-2"
              src={selectedOption.svg}
              alt={selectedOption.label}
            />
          )}
          <Button.Label>{selectedOption.label}</Button.Label>
          <Button.Icon icon={faCaretDown} size="sm" />
        </Button>
      </Select.Trigger>
      <Select.Portal container={container}>
        <Select.Content
          asChild
          position="popper"
          side="bottom"
          align="start"
          onCloseAutoFocus={onCloseAutoFocus}
        >
          <Card className="shadow-sm data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2">
            <Select.Viewport>
              <List gutterX="sm" gutterY="lg" border={false}>
                {options.map(({ icon, label, region: optionValue, svg }) => (
                  <Select.SelectItem
                    asChild
                    value={optionValue}
                    key={optionValue}
                  >
                    <List.Item className="group outline-none data-[highlighted]:bg-active">
                      <List.Content>
                        {icon && <Icon icon={icon} />}
                        {svg && <img src={svg} alt={label} />}
                        {label}
                      </List.Content>
                    </List.Item>
                  </Select.SelectItem>
                ))}
              </List>
            </Select.Viewport>
          </Card>
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  );
}
